import { RootState } from 'store/types';
import { createSelector } from 'reselect';
import dayjs from 'dayjs';
import { splitToArray } from 'modules/utils/ArrayUtils';
import { GlobalSearchItem } from 'store/global/types';
import { Problem } from '../types';

export const problemStateSelector = (state: RootState) => state.problems.problems;

export const loadingSelector = createSelector(problemStateSelector, problems => problems.loading);

export const problemsSelector = createSelector(problemStateSelector, problems => problems.data);
export const problemsRequestStateSelector = createSelector(
  problemStateSelector,
  problems => problems.requestState
);

const activeProblemStatuses = [
  'active',
  'recurring',
  'relapse',
  'well-controlled',
  'poorly-controlled'
];

const remissionProblemStatuses = ['remission', 'inactive'];

const resolvedProblemStatuses = ['resolved'];

const createProblemFilterByStatus = (statusList: string[]) => (p: Problem) =>
  statusList.includes((p.status || '').toLowerCase());

const sortByOnsetDate = (a: Problem, b: Problem) => dayjs(b.onsetDate).diff(a.onsetDate, 'day');

export const activeProblemsSelector = createSelector(problemsSelector, problems =>
  problems.filter(createProblemFilterByStatus(activeProblemStatuses)).sort(sortByOnsetDate)
);

export const remissionProblemsSelector = createSelector(problemsSelector, problems =>
  problems.filter(createProblemFilterByStatus(remissionProblemStatuses)).sort(sortByOnsetDate)
);

export const resolvedProblemsSelector = createSelector(problemsSelector, problems =>
  problems.filter(createProblemFilterByStatus(resolvedProblemStatuses)).sort(sortByOnsetDate)
);

export const problemsErrorSelector = createSelector(problemStateSelector, state => state.error);

/**
 * Share Problems
 */
export const shareProblemsSelector = (state: RootState) => state.problems.shareProblems;

export const shareProblemsSendingSelector = createSelector(
  shareProblemsSelector,
  shareProblems => shareProblems.isSendingProblems
);

export const shareProblemsDataSelector = createSelector(
  shareProblemsSelector,
  shareProblems => shareProblems.sharedProblemsData
);

/**
 * Download Problems
 */
export const problemsDownloadsSelector = (state: RootState) => state.problems.downloadProblems;

export const problemsDownloadingSelector = createSelector(
  problemsDownloadsSelector,
  downloadProblems => downloadProblems.isDownloading
);

export const problemsDownloadErrorSelector = createSelector(
  problemsDownloadsSelector,
  downloadProblems => downloadProblems.error
);

export const searchFormattedProblems = createSelector(activeProblemsSelector, data => {
  return data.map(
    (item: Problem): GlobalSearchItem => {
      const keywords = splitToArray(item?.name, ' ', [item?.category]);
      return {
        title: `Active Problem - ${item?.name}`,
        provider: item?.provider,
        date: item?.lastUpdated,
        keywords,
        navWebPath: '/u/health-record/problems',
        navParams: {},
        icon: 'MedicalHistoryIcon',
        category: 'Problems'
      };
    }
  );
});
